// src/features/student-management/ui/organisms/PersonForm/hooks/usePersonFormSync.ts
import { useEffect } from 'react';
import type { UseFormSetValue } from 'react-hook-form';

import type { Person } from '../../../../types/person';
import type { PersonFormValues } from '../personFormSchema';

interface UsePersonFormSyncParams {
  readonly person: Person | undefined;
  readonly isLoadingProfiles: boolean;
  readonly isLoadingFinancial: boolean;
  readonly setValue: UseFormSetValue<PersonFormValues>;
}

/**
 * Sincroniza campos de perfis (student/employee/teachingStaff) e financeiro quando
 * as queries opcionais concluem. Necessário porque o formulário monta antes dessas
 * queries terminarem — os campos inicializam como null e precisam ser populados
 * quando os dados chegam (inclusive em cache-hit + refetch).
 */
export function usePersonFormSync({
  person,
  isLoadingProfiles,
  isLoadingFinancial,
  setValue,
}: UsePersonFormSyncParams): void {
  useEffect(() => {
    if (isLoadingProfiles || !person) return;
    if (person.student) {
      setValue('student.ra', person.student.ra ?? null);
      setValue('student.curriculum', person.student.curriculum ?? null);
      setValue('student.codeUsp', person.student.codeUsp ?? null);
      setValue('student.perfilPublic', person.student.perfilPublic ?? false);
    }
    if (person.employee) {
      setValue('employee.postId', person.employee.postId ?? null);
      setValue('employee.codeBadge', person.employee.codeBadge ?? null);
      setValue('employee.dateStart', person.employee.dateStart ?? null);
      setValue('employee.dateFinish', person.employee.dateFinish ?? null);
      setValue('employee.type', person.employee.type ?? null);
      setValue(
        'employee.serviceRequestNotification',
        person.employee.serviceRequestNotification ?? false,
      );
    }
    if (person.teachingStaff) {
      setValue('teachingStaff.observation', person.teachingStaff.observation ?? null);
      setValue('teachingStaff.isUspTeaching', person.teachingStaff.isUspTeaching ?? false);
      setValue('teachingStaff.codeUSP', person.teachingStaff.codeUSP ?? null);
      setValue('teachingStaff.certNumber', person.teachingStaff.certNumber ?? null);
      setValue('teachingStaff.uspContractType', person.teachingStaff.uspContractType ?? null);
      setValue('teachingStaff.academicEducation', person.teachingStaff.academicEducation ?? null);
      setValue('teachingStaff.certWorkload', person.teachingStaff.certWorkload ?? null);
      setValue('teachingStaff.certExpirationDate', person.teachingStaff.certExpirationDate ?? null);
      setValue('teachingStaff.isLattes', person.teachingStaff.isLattes ?? false);
      setValue('teachingStaff.lattesUpdateDate', person.teachingStaff.lattesUpdateDate ?? null);
    }
  }, [isLoadingProfiles, person, setValue]);

  useEffect(() => {
    if (isLoadingFinancial || !person?.financial) return;
    setValue('financialBankName', person.financial.bankName ?? '');
    setValue('financialAgency', person.financial.agency ?? '');
    setValue('financialCheckingAccount', person.financial.checkingAccount ?? '');
    setValue('financialSavingAccount', person.financial.savingAccount ?? '');
    setValue('financialPaymentMethod', person.financial.paymentMethod ?? undefined);
    setValue('financialAccountType', person.financial.accountType ?? undefined);
  }, [isLoadingFinancial, person, setValue]);
}
